import { FC, useState } from 'react';

import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getAuth } from 'firebase/auth';
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';

import { makeAuthenticatedApiCall } from '../apiHelper';
import Dropzone from '../components/CreateCotation/DropZone';

const storage = getStorage();

interface OcrExtraction {
  text: string;
  type: 'manuscrit' | 'imprime' | 'tampon' | 'autre';
  confidence: number;
  location: string;
}

interface OcrResult {
  extractions: OcrExtraction[];
}

interface OcrApiResponse {
  success: boolean;
  data: OcrResult;
  model: string;
  usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
  error?: string;
}

interface ModelResult {
  result: OcrResult | null;
  model: string | null;
  usage: OcrApiResponse['usage'] | null;
  analyzing: boolean;
  error: string | null;
}

const TYPE_LABELS: Record<string, { label: string; color: 'primary' | 'secondary' | 'warning' | 'default' }> = {
  manuscrit: { label: 'Manuscrit', color: 'warning' },
  imprime: { label: 'Imprime', color: 'primary' },
  tampon: { label: 'Tampon', color: 'secondary' },
  autre: { label: 'Autre', color: 'default' },
};

const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.9) return '#4caf50';
  if (confidence >= 0.7) return '#ff9800';
  return '#f44336';
};

const INITIAL_MODEL_STATE: ModelResult = { result: null, model: null, usage: null, analyzing: false, error: null };

const OcrPage: FC = () => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mistral, setMistral] = useState<ModelResult>(INITIAL_MODEL_STATE);
  const [qwen, setQwen] = useState<ModelResult>(INITIAL_MODEL_STATE);
  const [snackError, setSnackError] = useState<string | null>(null);

  const auth = getAuth();

  const analyzeWithModel = async (
    url: string,
    endpoint: string,
    setState: React.Dispatch<React.SetStateAction<ModelResult>>,
  ) => {
    setState((prev) => ({ ...prev, analyzing: true, error: null, result: null, model: null, usage: null }));

    try {
      const response = await makeAuthenticatedApiCall<OcrApiResponse>('POST', endpoint, { imageUrl: url });
      const data = response.data;

      if (data.success) {
        setState((prev) => ({ ...prev, result: data.data, model: data.model, usage: data.usage, analyzing: false }));
      } else {
        setState((prev) => ({ ...prev, error: data.error || "Erreur lors de l'analyse", analyzing: false }));
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.status === 429
          ? 'Rate limit atteint. Veuillez réessayer dans quelques instants.'
          : "Erreur lors de l'analyse";
      setState((prev) => ({ ...prev, error: errorMessage, analyzing: false }));
    }
  };

  const analyzeImage = async (url: string) => {
    // Lancer Mistral d'abord, puis Qwen une fois terminé pour éviter
    // la surcharge/timeout côté OVH en appelant les deux modèles en parallèle.
    await analyzeWithModel(url, '/api/ocr/extract', setMistral);
    await analyzeWithModel(url, '/api/ocr/extract-qwen', setQwen);
  };

  const handleFilesAdded = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setMistral(INITIAL_MODEL_STATE);
    setQwen(INITIAL_MODEL_STATE);
    setSnackError(null);

    const uniqueId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    const fileExtension = file.name.split('.').pop() || 'jpg';
    const randomFileName = `${uniqueId}.${fileExtension}`;
    const storagePath = `ocr/${auth.currentUser?.uid}/${randomFileName}`;
    const storageRef = ref(storage, storagePath);

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (uploadError) => {
        console.error('Upload error:', uploadError);
        setSnackError("Erreur lors de l'upload de l'image");
        setUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setImageUrl(downloadURL);
        setUploading(false);
        analyzeImage(downloadURL);
      },
    );
  };

  const handleReset = async () => {
    if (imageUrl) {
      try {
        const fileName = imageUrl.split('/').pop()?.split('?')[0];
        if (fileName) {
          const storageRef = ref(storage, decodeURIComponent(fileName));
          await deleteObject(storageRef);
        }
      } catch {
        // Ignore delete errors on reset
      }
    }
    setImageUrl(null);
    setMistral(INITIAL_MODEL_STATE);
    setQwen(INITIAL_MODEL_STATE);
    setSnackError(null);
  };

  const renderResultPanel = (label: string, state: ModelResult) => (
    <Paper sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
      <Box
        sx={{
          p: 2,
          backgroundColor: '#262626',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{label}</Typography>
        {state.model && (
          <Typography sx={{ fontSize: 11, color: '#999' }}>
            {state.model}
          </Typography>
        )}
      </Box>

      {state.analyzing && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: '#FDBB5F', mb: 1 }} />
          <Typography variant="body2" sx={{ fontSize: 14 }}>
            Analyse en cours...
          </Typography>
        </Box>
      )}

      {state.error && (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{state.error}</Alert>
        </Box>
      )}

      {state.result && state.result.extractions.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontSize: 14 }}>
            Aucun texte detecte.
          </Typography>
        </Box>
      )}

      {state.result && state.result.extractions.length > 0 && (
        <>
          <Box sx={{ px: 2, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 13, color: '#666' }}>
              {state.result.extractions.length} extraction(s)
            </Typography>
            {state.usage && (
              <Typography sx={{ fontSize: 11, color: '#999' }}>
                {state.usage.total_tokens} tokens
              </Typography>
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Texte</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Confiance</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Position</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.result.extractions.map((extraction, index) => {
                  const typeInfo = TYPE_LABELS[extraction.type] || TYPE_LABELS.autre;
                  const confidencePercent = Math.round(extraction.confidence * 100);
                  const confidenceColor = getConfidenceColor(extraction.confidence);

                  return (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                      <TableCell sx={{ fontSize: 12 }}>{index + 1}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontFamily: extraction.type === 'manuscrit' ? 'cursive, Urbanist' : 'Urbanist',
                            fontStyle: extraction.type === 'manuscrit' ? 'italic' : 'normal',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {extraction.text}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={typeInfo.label} color={typeInfo.color} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: '#eee',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${confidencePercent}%`,
                                height: '100%',
                                backgroundColor: confidenceColor,
                                borderRadius: 3,
                              }}
                            />
                          </Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: confidenceColor }}>
                            {confidencePercent}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 11, color: '#666' }}>{extraction.location}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );

  return (
    <Box sx={{ padding: 4, maxWidth: 1400, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <DocumentScannerIcon sx={{ fontSize: 40, color: '#FDBB5F' }} />
        <Typography variant="h1" sx={{ fontSize: 32 }}>
          Extraction de texte (OCR IA)
        </Typography>
      </Box>

      {!imageUrl && !uploading && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, fontSize: 16 }}>
            Uploadez une image de document pour en extraire automatiquement tous les textes (manuscrits et imprimes).
          </Typography>
          <Dropzone onFilesAdded={handleFilesAdded} />
        </Paper>
      )}

      {uploading && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: 16 }}>
            Upload en cours...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ height: 10, '& .MuiLinearProgress-bar': { backgroundColor: '#FDBB5F' } }}
          />
        </Paper>
      )}

      {imageUrl && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ fontSize: 18 }}>
              Image analysee
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!mistral.analyzing && !qwen.analyzing && (
                <Button variant="contained" sx={{ backgroundColor: '#FDBB5F' }} onClick={() => analyzeImage(imageUrl)}>
                  Relancer l'analyse
                </Button>
              )}
              <Button variant="outlined" color="error" onClick={handleReset}>
                Nouvelle image
              </Button>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <a href={imageUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={imageUrl}
                alt="Document analyse"
                style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 8, cursor: 'pointer' }}
              />
            </a>
          </Box>
        </Paper>
      )}

      {imageUrl && (
        <Box sx={{ display: 'flex', gap: 3 }}>
          {renderResultPanel('Mistral Small 3.2', mistral)}
          {renderResultPanel('Qwen 2.5 VL 72B', qwen)}
        </Box>
      )}

      <Snackbar open={!!snackError} autoHideDuration={8000} onClose={() => setSnackError(null)}>
        <Alert severity="error" onClose={() => setSnackError(null)} variant="filled">
          {snackError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OcrPage;
